import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router";
import SignIn from "./pages/AuthPages/SignIn";
import NotFound from "./pages/OtherPage/NotFound";
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./components/common/ScrollToTop";
import DashboardEntry from "./pages/Dashboard/DashboardEntry";
import ShedsPage from "./pages/MasterData/ShedsPage";
import FlocksPage from "./pages/Inventory/FlocksPage";
import FlockDetailPage from "./pages/Inventory/FlockDetailPage";
import RawMaterialsPage from "./pages/MasterData/RawMaterialsPage";
import FeedFormulasPage from "./pages/MasterData/FeedFormulasPage";
import SuppliersPage from "./pages/MasterData/SuppliersPage";
import BuyersPage from "./pages/MasterData/BuyersPage";
import BreedsPage from "./pages/MasterData/BreedsPage";
import StandardCurvesPage from "./pages/MasterData/StandardCurvesPage";
import VaccinationSchedulePage from "./pages/MasterData/VaccinationSchedulePage";
import ExpenseCategoriesPage from "./pages/MasterData/ExpenseCategoriesPage";
import FeedDistributionPage from "./pages/DailyEntry/FeedDistributionPage";
import EggCollectionPage from "./pages/DailyEntry/EggCollectionPage";
import MortalityPage from "./pages/DailyEntry/MortalityPage";
import FlockMetricsPage from "./pages/DailyEntry/FlockMetricsPage";
import PurchasesPage from "./pages/Purchases/PurchasesPage";
import StockPage from "./pages/Inventory/StockPage";
import FeedProductionPage from "./pages/Inventory/FeedProductionPage";
import EggSalesPage from "./pages/Sales/EggSalesPage";
import BirdSalesPage from "./pages/Sales/BirdSalesPage";
import ManureSalesPage from "./pages/Sales/ManureSalesPage";
import BagSalesPage from "./pages/Sales/BagSalesPage";
import ExpensesPage from "./pages/Expenses/ExpensesPage";
import EggGradingPage from "./pages/Reports/EggGradingPage";
import PayablesPage from "./pages/Payables/PayablesPage";
import SupplierLedgerPage from "./pages/Payables/SupplierLedgerPage";
import ReceivablesPage from "./pages/Receivables/ReceivablesPage";
import BuyerLedgerPage from "./pages/Receivables/BuyerLedgerPage";
import ReportsPage from "./pages/Reports/ReportsPage";
import ProductionReportPage from "./pages/Reports/ProductionReportPage";
import EggStockReportPage from "./pages/Reports/EggStockReportPage";
import FeedInventoryReportPage from "./pages/Reports/FeedInventoryReportPage";
import UsersPage from "./pages/Admin/UsersPage";
import ActivityLogPage from "./pages/Admin/ActivityLogPage";
import FarmSettingsPage from "./pages/Settings/FarmSettingsPage";
import QRProfilePage from "./pages/Settings/QRProfilePage";
import DailyReportPage from "./pages/DailyReport/DailyReportPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SWUpdatePrompt from "./components/common/SWUpdatePrompt";
import OfflineIndicator from "./components/common/OfflineIndicator";
import PWAInstallPrompt from "./components/common/PWAInstallPrompt";
import FarmsPage from "./pages/System/FarmsPage";
import FarmDetailPage from "./pages/System/FarmDetailPage";
import { useAuthStore } from "./store/useAuthStore";

// LAZY: the owner-only finance and labour screens.
//
// They are a large share of the bundle and most sessions never open one —
// a supervisor in daily_report_mode sees two menu items. Precaching them
// into the service worker pushed the main chunk past Workbox's 2 MiB limit,
// and a farm on a phone connection should not download the general ledger
// to log a day's eggs.
const FarmPnLPage = lazy(() => import("./pages/Finance/FarmPnLPage"));
const FinanceCockpitPage = lazy(() => import("./pages/Finance/FinanceCockpitPage"));
const TrialBalancePage = lazy(() => import("./pages/Finance/TrialBalancePage"));
const ChartOfAccountsPage = lazy(() => import("./pages/Finance/ChartOfAccountsPage"));
const VouchersPage = lazy(() => import("./pages/Finance/VouchersPage"));
const GeneralLedgerPage = lazy(() => import("./pages/Finance/GeneralLedgerPage"));
const AttendancePage = lazy(() => import("./pages/Labour/AttendancePage"));
const PeoplePage = lazy(() => import("./pages/Labour/PeoplePage"));
const PayrollPage = lazy(() => import("./pages/Labour/PayrollPage"));
const AssetRegisterPage = lazy(() => import("./pages/Assets/AssetRegisterPage"));
const MaintenancePage = lazy(() => import("./pages/Assets/MaintenancePage"));
const SubsidiesPage = lazy(() => import("./pages/Subsidies/SubsidiesPage"));
const LoansPage = lazy(() => import("./pages/Loans/LoansPage"));
const CashFlowPage = lazy(() => import("./pages/CashFlow/CashFlowPage"));
const HealthPage = lazy(() => import("./pages/Health/HealthPage"));
const ProfitAndLossPage = lazy(() => import("./pages/Finance/ProfitAndLossPage"));
const BalanceSheetPage = lazy(() => import("./pages/Finance/BalanceSheetPage"));
const OpeningBalancesPage = lazy(() => import("./pages/Finance/OpeningBalancesPage"));
const NECCRatesPage = lazy(() => import("./pages/Finance/NECCRatesPage"));
const CollectionsPage = lazy(() => import("./pages/Finance/CollectionsPage"));
const PaymentsDuePage = lazy(() => import("./pages/Finance/PaymentsDuePage"));
const PriceRealisationPage = lazy(() => import("./pages/Finance/PriceRealisationPage"));
const FlockEconomicsComparisonPage = lazy(() => import("./pages/Finance/FlockEconomicsComparisonPage"));
const BankAccountsPage = lazy(() => import("./pages/Finance/BankAccountsPage"));
const BankAccountLedgerPage = lazy(() => import("./pages/Finance/BankAccountLedgerPage"));
const BankStatementDetailPage = lazy(() => import("./pages/Finance/BankStatementDetailPage"));

export default function App() {
  const { loadSession } = useAuthStore();

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  return (
    <Router>
      <ScrollToTop />
      <OfflineIndicator />
      <SWUpdatePrompt />
      <PWAInstallPrompt />
      {/* A lazy route needs a boundary. The fallback is deliberately quiet —
          these chunks load in a blink on any real connection, and a full-page
          spinner flashing on every navigation reads as slowness the app does
          not have. */}
      {/* data-route-pending: the page is not shown yet — the e2e page recorder
          (vite.stack.config.mts) waits until no such element is left. */}
      <Suspense fallback={<div data-route-pending className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>}>
      <Routes>
        {/* System admin routes */}
        <Route element={<ProtectedRoute requiredRoles={["admin"]} />}>
          <Route element={<AppLayout />}>
            <Route path="/system" element={<FarmsPage />} />
            <Route path="/system/farms/:id" element={<FarmDetailPage />} />
          </Route>
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index path="/" element={<DashboardEntry />} />

            {/* Daily Report (unified form) */}
            <Route path="/daily-report" element={<DailyReportPage />} />

            {/* Daily Entry */}
            <Route path="/feed" element={<FeedDistributionPage />} />
            <Route path="/eggs" element={<EggCollectionPage />} />
            <Route path="/mortality" element={<MortalityPage />} />
            <Route path="/flock-metrics" element={<FlockMetricsPage />} />

            {/* Purchases */}
            <Route path="/purchases" element={<PurchasesPage />} />

            {/* Inventory */}
            <Route path="/stock" element={<StockPage />} />
            <Route path="/feed-production" element={<FeedProductionPage />} />

            {/* Sales */}
            <Route path="/egg-sales" element={<EggSalesPage />} />
            <Route path="/bird-sales" element={<BirdSalesPage />} />
            <Route path="/manure-sales" element={<ManureSalesPage />} />
            <Route path="/bag-sales" element={<BagSalesPage />} />

            {/* Labour. Attendance is marked by whoever walks the sheds, so it
                is open to every authenticated role — the people holding the
                sheet have to be able to mark it. The register is owner-only,
                below, because a rate is wage information. */}
            <Route path="/labour/attendance" element={<AttendancePage />} />
            <Route path="/assets/maintenance" element={<MaintenancePage />} />

            {/* Expenses & AP/AR */}
            {/* Owner-only finance cockpit (backend also RequireRole("owner")) */}
            <Route element={<ProtectedRoute requiredRoles={["owner"]} />}>
              <Route path="/finance/cockpit" element={<FinanceCockpitPage />} />
              <Route path="/finance/vouchers" element={<VouchersPage />} />
              <Route path="/finance/general-ledger" element={<GeneralLedgerPage />} />
              <Route path="/labour/people" element={<PeoplePage />} />
              <Route path="/labour/payroll" element={<PayrollPage />} />
              <Route path="/assets/register" element={<AssetRegisterPage />} />
              <Route path="/finance/subsidies" element={<SubsidiesPage />} />
              <Route path="/finance/loans" element={<LoansPage />} />
              <Route path="/finance/cash-flow" element={<CashFlowPage />} />
              <Route path="/finance/chart-of-accounts" element={<ChartOfAccountsPage />} />
              <Route path="/finance/trial-balance" element={<TrialBalancePage />} />
              <Route path="/finance/profit-and-loss" element={<ProfitAndLossPage />} />
              <Route path="/finance/balance-sheet" element={<BalanceSheetPage />} />
              <Route path="/finance/opening-balances" element={<OpeningBalancesPage />} />
              <Route path="/finance/price-realisation" element={<PriceRealisationPage />} />
            </Route>
            {/* Owner OR MANAGER, matching the backend's RequireRole on
                POST /necc-rates. This route sat in the owner-only block above
                while the sidebar showed the link to managers — so a manager
                clicked it and ProtectedRoute bounced them to the dashboard
                with no message at all. Three places have to agree about who
                may reach a screen: the router, the sidebar, and the API. */}
            <Route element={<ProtectedRoute requiredRoles={["owner", "manager"]} />}>
              <Route path="/finance/necc-rates" element={<NECCRatesPage />} />
              <Route path="/finance/collections" element={<CollectionsPage />} />
              {/* Egg grading is owner+manager like collections: a manager
                  sells the eggs and is the one who can act on a worsening
                  mix. API, route and sidebar all agree. */}
              <Route path="/egg-grading" element={<EggGradingPage />} />
            </Route>
            {/* Payments due is OWNER ONLY — the API gates it that way and the
                sidebar hides it from everyone else. All three must agree. */}
            <Route element={<ProtectedRoute requiredRoles={["owner"]} />}>
              <Route path="/finance/payments-due" element={<PaymentsDuePage />} />
            </Route>
            {/* Owner and viewer, matching RequireRole on /economics. */}
            <Route element={<ProtectedRoute requiredRoles={["owner", "viewer"]} />}>
              <Route path="/farm-pnl" element={<FarmPnLPage />} />
              <Route path="/flock-economics" element={<FlockEconomicsComparisonPage />} />
            </Route>
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/payables" element={<PayablesPage />} />
            <Route path="/payables/supplier/:id" element={<SupplierLedgerPage />} />
            <Route path="/receivables" element={<ReceivablesPage />} />
            <Route path="/receivables/buyer/:id" element={<BuyerLedgerPage />} />
            {/* Bank is owner-only in the API and the sidebar. Ungated here, a
                typed URL opened a page whose every call failed. */}
            <Route element={<ProtectedRoute requiredRoles={["owner"]} />}>
              <Route path="/finance/bank-accounts" element={<BankAccountsPage />} />
              <Route path="/finance/bank-accounts/:accountNumber" element={<BankAccountLedgerPage />} />
              {/* The Statement Uploads page merged into Bank (2026-10); old links land there. */}
              <Route path="/finance/bank-statements" element={<Navigate to="/finance/bank-accounts" replace />} />
              <Route path="/finance/bank-statements/:id" element={<BankStatementDetailPage />} />
            </Route>

            {/* Reports. The CSV exports and the stock reports read
                /api/reports/*, which is owner + viewer; ungated, a manager or
                supervisor with the URL opened a page whose data was refused
                (found by the page sweep, docs/E2E_JOURNEY_PLAN.md F2). */}
            <Route path="/production-report" element={<ProductionReportPage />} />
            <Route element={<ProtectedRoute requiredRoles={["owner", "viewer"]} />}>
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/egg-stock-report" element={<EggStockReportPage />} />
              <Route path="/feed-inventory-report" element={<FeedInventoryReportPage />} />
            </Route>

            {/* Master Data */}
            <Route path="/sheds" element={<ShedsPage />} />
            <Route path="/flocks" element={<FlocksPage />} />
            <Route path="/flocks/:id" element={<FlockDetailPage />} />
            <Route path="/raw-materials" element={<RawMaterialsPage />} />
            <Route path="/feed-formulas" element={<FeedFormulasPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/buyers" element={<BuyersPage />} />
            <Route path="/breeds" element={<BreedsPage />} />
            <Route path="/standard-curves" element={<StandardCurvesPage />} />
            <Route path="/vaccination-schedule" element={<VaccinationSchedulePage />} />
            <Route path="/flock-health" element={<HealthPage />} />
            <Route path="/expense-categories" element={<ExpenseCategoriesPage />} />

            {/* Settings and admin are the OWNER's in the API: farm settings
                save is owner-only (and the page shows the farm's bank
                details), users / activity log / QR profiles likewise. */}
            <Route element={<ProtectedRoute requiredRoles={["owner"]} />}>
              <Route path="/farm-settings" element={<FarmSettingsPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/activity-log" element={<ActivityLogPage />} />
              <Route path="/qr-profile" element={<QRProfilePage />} />
            </Route>
          </Route>
        </Route>

        {/* Auth */}
        <Route path="/signin" element={<SignIn />} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Router>
  );
}
